/**
 * @fileoverview Browser Console Utilities for Creately Legacy Converter
 * 
 * Exposes console commands for converting and importing old Creately exports
 * in the browser. Accessible via window.createlyConverter
 */

import { convertCreatelyExport, type CreatelyLegacyConverterOptions } from './createlyLegacyConverter';
import type { Scene } from '../core/models/scene';
import { getGravityUserRegion } from '../hooks/useAuth';

type CreatelyLegacyConverterWindow = Window & {
  __CREATELY_LEGACY_IMAGE_BASE_URL__?: string;
  __CREATELY_LEGACY_IMAGE_REGION__?: string;
};

function getConverterOptions(): CreatelyLegacyConverterOptions {
  if (typeof window === 'undefined') return {};

  const w = window as CreatelyLegacyConverterWindow;
  const legacyCustomImageBaseUrl = w.__CREATELY_LEGACY_IMAGE_BASE_URL__?.trim();
  const overrideRegion = w.__CREATELY_LEGACY_IMAGE_REGION__?.trim();
  const region = overrideRegion || getGravityUserRegion() || 'us';

  return {
    region,
    ...(legacyCustomImageBaseUrl ? { legacyCustomImageBaseUrl } : {}),
  };
}

/**
 * Browser console utilities for Creately legacy format conversion
 */
export class CreatelyConverterBrowser {
  /**
   * Convert Creately export data to Scene format
   * 
   * @param createlyData - The old Creately export data (JSON object)
   * @returns Converted Scene object
   * 
   * @example
   * ```javascript
   * // In browser console:
   * const data = JSON.parse(await fetch('/path/to/export.json').then(r => r.text()));
   * const scene = window.createlyConverter.convert(data);
   * console.log(`Converted ${scene.nodes.length} nodes and ${scene.edges.length} edges`);
   * ```
   */
  convert(createlyData: unknown): Scene {
    try {
      const scene = convertCreatelyExport(createlyData, getConverterOptions());
      console.log('✓ Conversion successful:', {
        nodes: scene.nodes.length,
        edges: scene.edges.length,
        scene
      });
      return scene;
    } catch (error) {
      console.error('✗ Conversion failed:', error);
      throw error;
    }
  }

  /**
   * Convert and load a Creately export file
   * 
   * This will prompt the user to select a file, convert it, and load it into the diagram.
   * 
   * @example
   * ```javascript
   * // In browser console:
   * window.createlyConverter.convertAndLoad();
   * ```
   */
  async convertAndLoad(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.style.display = 'none';
      
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        try {
          // Read file
          const text = await file.text();
          const createlyData = JSON.parse(text);
          
          console.log('📄 Reading file:', file.name);
          
          // Convert
          const scene = this.convert(createlyData);
          
          // Load into diagram store
          await this.loadScene(scene);
          
          console.log('✓ Successfully loaded converted diagram');
          resolve();
        } catch (error) {
          console.error('✗ Failed to convert and load:', error);
          reject(error);
        } finally {
          document.body.removeChild(input);
        }
      };

      input.oncancel = () => {
        document.body.removeChild(input);
        reject(new Error('File selection cancelled'));
      };

      document.body.appendChild(input);
      input.click();
    });
  }

  /**
   * Convert from JSON string or object and load into diagram
   * 
   * @param data - Creately export data as JSON string or object
   * @param autoLoad - Whether to automatically load into diagram (default: true)
   * @returns Converted Scene object
   * 
   * @example
   * ```javascript
   * // In browser console:
   * const jsonString = '{"shapesToCopy": [...]}';
   * const scene = await window.createlyConverter.convertFromString(jsonString);
   * ```
   */
  async convertFromString(
    data: string | object,
    autoLoad: boolean = true
  ): Promise<Scene> {
    try {
      const createlyData = typeof data === 'string' ? JSON.parse(data) : data;
      const scene = this.convert(createlyData);
      
      if (autoLoad) {
        await this.loadScene(scene);
        console.log('✓ Scene loaded into diagram');
      }
      
      return scene;
    } catch (error) {
      console.error('✗ Failed to convert from string:', error);
      throw error;
    }
  }

  /**
   * Load a Scene into the diagram store
   * 
   * @param scene - Scene object to load
   * 
   * @example
   * ```javascript
   * // In browser console:
   * const scene = { version: 1, nodes: [...], edges: [...] };
   * await window.createlyConverter.loadScene(scene);
   * ```
   */
  async loadScene(scene: Scene): Promise<void> {
    try {
      // Dynamically import to avoid circular dependencies
      const { useDiagramStore } = await import('../stores/diagramStore');
      const store = useDiagramStore.getState();
      
      if (!store.loadScene) {
        throw new Error('Diagram store not available. Make sure you are on a diagram page.');
      }
      
      store.loadScene(scene);
      console.log('✓ Scene loaded:', {
        nodes: scene.nodes.length,
        edges: scene.edges.length
      });
    } catch (error) {
      console.error('✗ Failed to load scene:', error);
      throw error;
    }
  }

  /**
   * Download the current diagram as a converted Scene JSON file
   * 
   * @example
   * ```javascript
   * // In browser console:
   * window.createlyConverter.exportCurrentDiagram();
   * ```
   */
  async exportCurrentDiagram(): Promise<void> {
    try {
      const { useDiagramStore } = await import('../stores/diagramStore');
      const store = useDiagramStore.getState();
      
      if (!store.exportScene) {
        throw new Error('Diagram store not available');
      }
      
      const scene = store.exportScene();
      const json = JSON.stringify(scene, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `genogram-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✓ Diagram exported');
    } catch (error) {
      console.error('✗ Failed to export diagram:', error);
      throw error;
    }
  }
}

// Create singleton instance
const converter = new CreatelyConverterBrowser();

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).createlyConverter = converter;
  
  // Also expose as a shorter alias
  (window as any).cc = converter;
  
  console.log(
    '%cCreately Converter',
    'color: #4CAF50; font-weight: bold; font-size: 14px;'
  );
  console.log('Available commands:');
  console.log('  window.createlyConverter.convertAndLoad() - Convert and load a Creately export file');
  console.log('  window.createlyConverter.convert(data) - Convert Creately data to Scene format');
  console.log('  window.createlyConverter.convertFromString(json) - Convert from JSON string');
  console.log('  window.createlyConverter.loadScene(scene) - Load a Scene into the diagram');
  console.log('  window.createlyConverter.exportCurrentDiagram() - Export current diagram as JSON');
  console.log('  window.cc - Shorter alias for createlyConverter');
}

export default converter;

